"use client";

import React from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
export default function Outdoors() {
  const shouldReduceMotion = useReducedMotion();
  const initialMotion = shouldReduceMotion ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 16
  };
  const animateMotion = {
    opacity: 1,
    y: 0
  };
  const transitionConfig = shouldReduceMotion ? {
    duration: 0
  } : {
    duration: 0.4,
    ease: [0.16, 1, 0.3, 1]
  };
  const keyHighlights = ["Screened porches & sunrooms", "Custom hardwood & composite decks", "Patios & built-in outdoor kitchens"];
  return <section id="extend-your-home-outdoors" data-nav="light" className="relative bg-background py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="mx-auto max-w-7xl">
        <motion.div initial={initialMotion} whileInView={animateMotion} viewport={{
        once: true,
        amount: 0.2
      }} transition={transitionConfig} className="relative mx-auto max-w-4xl overflow-hidden rounded-2xl border border-border bg-card/90 backdrop-blur-md p-8 sm:p-12 lg:p-16 shadow-xl text-center">
          {/* Architectural Copper Top Hairline */}
          <div className="absolute top-0 inset-x-0 h-1 bg-primary" />

          {/* Eyebrow / Architectural Tag */}
          <div className="flex justify-center mb-6">
            <Badge variant="outline" className="px-3.5 py-1 text-xs font-semibold tracking-wider uppercase border-border bg-secondary text-secondary-foreground">
              <Sparkles className="size-3 text-primary mr-1.5" />
              Outdoor living & decks
            </Badge>
          </div>

          {/* Heading */}
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground tracking-tight leading-tight max-w-2xl mx-auto">
            Extend your living space outdoors
          </h2>

          {/* Supporting Copy */}
          <p className="mt-4 sm:mt-6 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed max-w-2xl mx-auto">
            From covered screened porches and custom composite decks to flagstone patios and outdoor cooking stations, we build enduring outdoor spaces tailored to your Decatur home.
          </p>

          {/* Quick Feature Badges */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2.5 sm:gap-3 text-xs sm:text-sm font-medium text-muted-foreground">
            {keyHighlights.map((highlight, index) => <span key={index} data-index={index} className="inline-flex items-center px-3 py-1.5 rounded-md bg-secondary/70 border border-border text-foreground text-xs">
                {highlight}
              </span>)}
          </div>

          {/* Actions */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-8 py-3.5 shadow-md transition-[color,background-color,border-color,box-shadow]">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                Request an estimate
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border bg-background/80 text-foreground hover:bg-secondary font-semibold px-7 py-3.5 transition-[color,background-color,border-color,box-shadow]">
              <Link to="/ProjectsPage">
                View recent projects
              </Link>
            </Button>
          </div>

          {/* Micro Trust Note */}
          <p className="mt-6 text-xs text-muted-foreground">
            Serving Oakhurst, Winnona Park, Great Lakes, and all Decatur neighborhoods.
          </p>
        </motion.div>
      </div>
    </section>;
}